import { jsx, Fragment } from 'react/jsx-runtime';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';
import { useContext, useRef, useMemo, useEffect, createElement, createContext as createContext$1 } from 'react';

function generateInitialState(value) {
  return {
    getState: function getState() {
      return value;
    },
    getInitialState: function getInitialState() {
      return value;
    },
    subscribe: function subscribe() {
      return function () {};
    }
  };
}
function createProvider(Provider) {
  return function (_ref) {
    var value = _ref.value,
      children = _ref.children;
    var ref = useRef();
    if (!ref.current) {
      var state;
      var initialState = state = value;
      var listeners = new Set();
      var getState = function getState() {
        return state;
      };
      var getInitialState = function getInitialState() {
        return initialState;
      };
      var setState = function setState(nextState) {
        if (!Object.is(nextState, state)) {
          var previousState = state;
          state = nextState;
          listeners.forEach(function (listener) {
            return listener(state, previousState);
          });
        }
      };
      var subscribe = function subscribe(listener) {
        listeners.add(listener);
        return function () {
          return listeners["delete"](listener);
        };
      };
      ref.current = {
        setState: setState,
        getState: getState,
        subscribe: subscribe,
        getInitialState: getInitialState
      };
    }
    var contextValues = useMemo(function () {
      return ref.current ? {
        getState: ref.current.getState,
        getInitialState: ref.current.getInitialState,
        subscribe: ref.current.subscribe
      } : generateInitialState(value);
    }, [ref.current]);
    useEffect(function () {
      if (ref.current) {
        ref.current.setState(value);
      }
    }, [value]);
    return /*#__PURE__*/createElement(Provider, {
      value: contextValues
    }, children);
  };
}
function createConsumer(context) {
  return function (_ref2) {
    var children = _ref2.children,
      _ref2$selector = _ref2.selector,
      selector = _ref2$selector === void 0 ? function (state) {
        return state;
      } : _ref2$selector;
    var values = useContextWithSelector(context, selector);
    return jsx(Fragment, {
      children: children(values)
    });
  };
}
function createContext(defaultValue) {
  var _context = /*#__PURE__*/createContext$1(generateInitialState(defaultValue));
  var context = _context;
  context._Provider = _context.Provider;
  context._Consumer = _context.Consumer;
  context.Provider = createProvider(_context.Provider);
  context.Consumer = createConsumer(context);
  context.useContext = function (selector) {
    return useContextWithSelector(context, selector);
  };
  return context;
}
function useContextWithSelector(context) {
  var selector = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (state) {
    return state;
  };
  var _useContext = useContext(context),
    getState = _useContext.getState,
    subscribe = _useContext.subscribe,
    getInitialState = _useContext.getInitialState;
  return useSyncExternalStoreWithSelector(subscribe, getState, getInitialState, selector);
}

export { createContext, useContextWithSelector };
